/**
 * PortalEmbedSettings - Embed code settings for Customer Portal
 * Including service selection for public booking page
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Code, ArrowLeft, Copy, Check, ExternalLink, Loader2, Calendar, Save, Zap, Snowflake, Dumbbell, MessageCircle, Bot, Mail, Shield, Eye, Palette, CreditCard, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmbedStyleCard from '../components/admin/EmbedStyleCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Service icons mapping
const getServiceIcon = (serviceName) => {
  const name = serviceName.toLowerCase();
  if (name.includes('ems')) return Zap;
  if (name.includes('kryo') || name.includes('cryo')) return Snowflake;
  if (name.includes('beratung') || name.includes('consult')) return MessageCircle;
  return Dumbbell;
};

const PortalEmbedSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedCode, setEmbedCode] = useState('');
  // Full portal embed (with navigation)
  const [fullPortalUrl, setFullPortalUrl] = useState('');
  const [fullPortalCode, setFullPortalCode] = useState('');
  const [copiedFullPortal, setCopiedFullPortal] = useState(false);
  
  const [publicBookingUrl, setPublicBookingUrl] = useState('');
  const [publicBookingCode, setPublicBookingCode] = useState('');
  const [scheduleUrl, setScheduleUrl] = useState('');
  const [scheduleCode, setScheduleCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedPublic, setCopiedPublic] = useState(false);
  const [copiedSchedule, setCopiedSchedule] = useState(false);
  const [pricingUrl, setPricingUrl] = useState('');
  const [pricingCode, setPricingCode] = useState('');
  const [copiedPricing, setCopiedPricing] = useState(false);
  const [clickStats, setClickStats] = useState([]);
  const [displaySettings, setDisplaySettings] = useState(null);
  const [savingDisplay, setSavingDisplay] = useState(false);
  const [pricingData, setPricingData] = useState(null);
  
  // Custom Portal URL
  const [customPortalUrl, setCustomPortalUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  
  // Services
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Widget Settings
  const [widgetSettings, setWidgetSettings] = useState({
    enabled: true,
    verification_required_for_booking: true,
    verification_methods: ['email'],
    widget_title: 'StrongFit Chat',
    welcome_message: 'Hallo! 👋 Wie kann ich dir helfen?',
    primary_color: '#C9A227',
    background_color: '#1A1A1A',
    booking_enabled: true,
    waitlist_enabled: true,
    faq_enabled: true,
    // Social Links
    whatsapp_number: '41764449996',
    instagram_handle: 'strongfit_wetzikon',
    facebook_page: 'strongfitwetzikon'
  });
  const [widgetEmbedCode, setWidgetEmbedCode] = useState({ popup_code: '', iframe_code: '', widget_url: '' });
  const [savingWidget, setSavingWidget] = useState(false);
  const [copiedWidget, setCopiedWidget] = useState(null);

  // Iter 318 (Feb 2026): Per-Studio iFrame-Generator. Admin kann pro
  // Studio die passenden Codes generieren – PublicBooking/Schedule/Pricing
  // unterstützen `?studio=<slug>` bereits, hier hängen wir es nur an die
  // generierten URLs an.
  const [embedStudios, setEmbedStudios] = useState([]);
  const [embedStudioSlug, setEmbedStudioSlug] = useState(''); // '' = alle Studios
  // Basis-URLs ohne studio-Param (werden in loadData gesetzt). Die
  // angezeigten URLs/Codes werden in einem zweiten Effekt daraus
  // abgeleitet — damit der Studio-Switcher live alle vier Embed-Blöcke
  // aktualisiert ohne dass loadData neu läuft.
  const [bookingBaseUrl, setBookingBaseUrl] = useState('');
  const [scheduleBaseUrl, setScheduleBaseUrl] = useState('');
  const [pricingBaseUrl, setPricingBaseUrl] = useState('');
  // 06/2026 Mandanten-Isolation: Slug an ALLE Einbettungs-Links hängen
  const [embedTenantSlug, setEmbedTenantSlug] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Studios für den Picker laden (öffentlicher Endpoint reicht)
  useEffect(() => {
    axios.get(`${API}/public/locations`)
      .then(res => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : (raw?.studios || []);
        setEmbedStudios(list.filter(s => s && s.studio_id));
      })
      .catch(() => setEmbedStudios([]));
  }, []);

  // URLs + iframe-Codes neu generieren wenn Studio-Auswahl wechselt.
  useEffect(() => {
    const appendStudio = (url) => {
      if (!url) return '';
      if (!embedStudioSlug) return url;
      // Bereits gesetzten studio-Param vorher rauswerfen
      const cleaned = url.replace(/([?&])studio=[^&]*/g, '$1').replace(/&$/, '').replace(/\?$/, '');
      const sep = cleaned.includes('?') ? '&' : '?';
      return `${cleaned}${sep}studio=${embedStudioSlug}`;
    };
    const wrap = (url, title, height = '800px') => url ? `<iframe 
  src="${url}" 
  style="width: 100%; height: ${height}; border: none; border-radius: 8px; background: transparent;"
  title="${title}"
  allow="clipboard-write"
  allowtransparency="true"
></iframe>` : '';

    const finalBookingUrl  = appendStudio(bookingBaseUrl);
    const finalScheduleUrl = appendStudio(scheduleBaseUrl);
    const finalPricingUrl  = appendStudio(pricingBaseUrl);

    setPublicBookingUrl(finalBookingUrl);
    setScheduleUrl(finalScheduleUrl);
    setPricingUrl(finalPricingUrl);
    setPublicBookingCode(wrap(finalBookingUrl, 'Termin buchen'));
    setScheduleCode(wrap(finalScheduleUrl, 'Kursplan', '700px'));
    setPricingCode(wrap(finalPricingUrl, 'Preisliste'));
  }, [embedStudioSlug, bookingBaseUrl, scheduleBaseUrl, pricingBaseUrl]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, servicesRes, widgetRes, widgetCodeRes, embedInfoRes] = await Promise.all([
        axios.get(`${API}/portal/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/widget/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/widget/embed-code`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/resources/embed-info`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
      ]);

      // Mandanten-Slug für die Isolation aller Einbettungs-Links
      const tenantSlug = (embedInfoRes.data?.tenant_slug || '').trim();
      setEmbedTenantSlug(tenantSlug);
      const addTenant = (u) => {
        if (!tenantSlug || !u) return u;
        if (/[?&]tenant=/.test(u)) return u;
        return u + (u.includes('?') ? '&' : '?') + 'tenant=' + encodeURIComponent(tenantSlug);
      };
      
      // Get base URL from settings or use current origin (works across all environments)
      const customUrl = settingsRes.data.custom_portal_url;
      // Remove trailing slash and /portal path to get base URL
      // Use window.location.origin so links always match the current domain (e.g. app.strongfit.ch)
      const portalBaseUrl = customUrl 
        ? customUrl.replace(/\/portal.*$/, '').replace(/\/$/, '') 
        : window.location.origin;
      
      // Booking-only embed (embed=true) - no navigation
      const portalEmbedUrl = addTenant(settingsRes.data.embed_url || `${portalBaseUrl}/portal?embed=true`);
      setEmbedUrl(portalEmbedUrl);
      setEmbedCode(`<iframe 
  src="${portalEmbedUrl}" 
  style="width: 100%; height: 800px; border: none; border-radius: 8px; background: transparent;"
  title="Terminbuchung"
  allow="clipboard-write"
  allowtransparency="true"
></iframe>`);

      // Full portal embed (embed=full) - WITH navigation tabs
      const fullPortalEmbedUrl = portalEmbedUrl.replace('embed=true', 'embed=full');
      setFullPortalUrl(fullPortalEmbedUrl);
      setFullPortalCode(`<iframe 
  src="${fullPortalEmbedUrl}" 
  style="width: 100%; height: 900px; border: none; border-radius: 8px; background: transparent;"
  title="Kundenportal"
  allow="clipboard-write"
  allowtransparency="true"
></iframe>`);

      setSelectedServices(settingsRes.data.public_booking_services || []);
      setCustomPortalUrl(settingsRes.data.custom_portal_url || '');
      
      // Filter active services
      setServices(servicesRes.data.filter(s => s.is_active !== false));
      
      // Widget settings
      if (widgetRes.data) {
        setWidgetSettings(prev => ({ ...prev, ...widgetRes.data }));
      }
      if (widgetCodeRes.data) {
        setWidgetEmbedCode(widgetCodeRes.data);
      }
      
      // Generate public booking URLs with embed=true for clean iframe embedding
      // Use window.location.origin so links always match the current domain
      const bookingBaseUrl = customUrl 
        ? customUrl.replace(/\/portal.*$/, '').replace(/\/$/, '') 
        : window.location.origin;
      const bookingUrl = addTenant(`${bookingBaseUrl}/buchen?embed=true`);
      // Basis-URL für den Studio-Switcher merken – die effektiven URLs
      // (publicBookingUrl/scheduleUrl/pricingUrl) werden im zweiten
      // useEffect je nach gewähltem Studio berechnet.
      setBookingBaseUrl(bookingUrl);

      // Generate schedule/Kursplan URLs with embed=true
      const kursplanUrl = addTenant(`${bookingBaseUrl}/kursplan?embed=true`);
      setScheduleBaseUrl(kursplanUrl);

      // Generate pricing/Preisliste URLs with embed=true
      const preislisteUrl = addTenant(`${bookingBaseUrl}/preisliste?embed=true`);
      setPricingBaseUrl(preislisteUrl);

      // Load click stats
      try {
        const statsRes = await axios.get(`${API}/public/pricing/click-stats`);
        setClickStats(statsRes.data || []);
      } catch (e) {}

      // Load display settings
      try {
        const displayRes = await axios.get(`${API}/public/pricing/display-settings`, { headers: { Authorization: `Bearer ${token}` } });
        setDisplaySettings(displayRes.data || null);
      } catch (e) {}

      // Load pricing data for service names
      try {
        const pricingRes = await axios.get(`${API}/public/pricing`);
        setPricingData(pricingRes.data || null);
      } catch (e) {}
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomUrl = async () => {
    setSavingUrl(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/portal/settings/custom-url`, {
        custom_portal_url: customPortalUrl || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Save response:', response.data);
      toast.success('Portal-URL gespeichert');
      // Reload to get updated embed URLs
      loadData();
    } catch (error) {
      console.error('Save custom URL error:', error);
      toast.error('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingUrl(false);
    }
  };

  const handleSaveServices = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, {
        public_booking_services: selectedServices
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Services gespeichert');
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (serviceId) => {
    if (selectedServices.includes(serviceId)) {
      setSelectedServices(selectedServices.filter(id => id !== serviceId));
    } else {
      setSelectedServices([...selectedServices, serviceId]);
    }
  };

  const copyToClipboard = (text, isPublic = false) => {
    navigator.clipboard.writeText(text);
    if (isPublic) {
      setCopiedPublic(true);
      setTimeout(() => setCopiedPublic(false), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    toast.success('In Zwischenablage kopiert');
  };

  const copyWidgetCode = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedWidget(type);
    toast.success('In Zwischenablage kopiert');
    setTimeout(() => setCopiedWidget(null), 2000);
  };

  const handleSaveWidgetSettings = async () => {
    setSavingWidget(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/widget/settings`, widgetSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Widget-Einstellungen gespeichert');
      // Reload embed codes
      const codeRes = await axios.get(`${API}/widget/embed-code`, { headers: { Authorization: `Bearer ${token}` } });
      setWidgetEmbedCode(codeRes.data);
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSavingWidget(false);
    }
  };

  const toggleWidgetVerificationMethod = (method) => {
    const current = widgetSettings.verification_methods || [];
    if (current.includes(method)) {
      setWidgetSettings(prev => ({
        ...prev,
        verification_methods: current.filter(m => m !== method)
      }));
    } else {
      setWidgetSettings(prev => ({
        ...prev,
        verification_methods: [...current, method]
      }));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/portal-settings')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <Code className="h-6 w-6 text-gray-600" /> Einbettung
      </h1>

      <div className="space-y-6">
        {/* Custom Portal URL for Production */}
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-blue-600" />
              <CardTitle>Eigene Portal-URL (Live-Betrieb)</CardTitle>
            </div>
            <CardDescription>
              Gib hier deine eigene Domain ein, wenn du das Portal auf deiner Website einbetten möchtest.
              Diese URL wird auch in den E-Mails an Kunden verwendet (z.B. Passwort-Links).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="customUrl">Portal-URL</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  id="customUrl"
                  value={customPortalUrl} 
                  onChange={(e) => setCustomPortalUrl(e.target.value)}
                  placeholder="z.B. https://portal.strongfit.ch oder https://strongfit.ch/portal"
                  className="font-mono text-sm flex-1"
                />
                <Button 
                  onClick={handleSaveCustomUrl}
                  disabled={savingUrl}
                >
                  {savingUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Speichern
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Leer lassen = Standard-URL ({BASE_URL}/portal)
              </p>
            </div>
            
            {customPortalUrl && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  ✅ <strong>Aktive Live-URL:</strong> {customPortalUrl}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Diese URL wird in allen E-Mails an Kunden verwendet.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Direct URL - Booking Only */}
        <Card>
          <CardHeader>
            <CardTitle>Portal URL (Nur Buchung)</CardTitle>
            <CardDescription>Direkter Link zur Buchungsseite - ohne Navigation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input value={embedUrl} readOnly className="font-mono text-sm flex-1" />
              <Button variant="outline" onClick={() => copyToClipboard(embedUrl)}>
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Button variant="outline" onClick={() => window.open(embedUrl, '_blank')}>
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Embed Code - Booking Only */}
        <Card>
          <CardHeader>
            <CardTitle>iFrame Einbettungscode (Nur Buchung)</CardTitle>
            <CardDescription>Zeigt nur die Buchungsseite - ideal für Landing Pages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
                <code>{embedCode}</code>
              </pre>
              <Button 
                variant="outline" 
                size="sm" 
                className="absolute top-2 right-2"
                onClick={() => copyToClipboard(embedCode)}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Full Portal Embed - WITH NAVIGATION */}
        <Card className="border-2 border-green-200 bg-green-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Code className="h-5 w-5 text-green-600" />
              <CardTitle>Komplettes Kundenportal</CardTitle>
            </div>
            <CardDescription>
              Das vollständige Kundenportal mit Navigation (Home, Buchen, Termine, Pakete, etc.)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* URL */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">Portal URL (Mit Navigation)</Label>
              <div className="flex gap-2">
                <Input value={fullPortalUrl} readOnly className="font-mono text-sm flex-1" />
                <Button 
                  variant="outline" 
                  onClick={() => {
                    navigator.clipboard.writeText(fullPortalUrl);
                    setCopiedFullPortal(true);
                    setTimeout(() => setCopiedFullPortal(false), 2000);
                    toast.success('URL kopiert!');
                  }}
                >
                  {copiedFullPortal ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="outline" onClick={() => window.open(fullPortalUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Embed Code */}
            <div>
              <Label className="text-sm text-gray-600 mb-2 block">iFrame Einbettungscode</Label>
              <div className="relative">
                <pre className="bg-white border p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{fullPortalCode}</code>
                </pre>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2"
                  onClick={() => {
                    navigator.clipboard.writeText(fullPortalCode);
                    setCopiedFullPortal(true);
                    setTimeout(() => setCopiedFullPortal(false), 2000);
                    toast.success('Code kopiert!');
                  }}
                >
                  {copiedFullPortal ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-green-100 border border-green-300 rounded-lg p-4 text-sm text-green-800">
              <strong>Tipp:</strong> Verwenden Sie diesen Code, wenn Sie das komplette Kundenportal mit allen Funktionen 
              (Termine, Pakete, Profil, etc.) auf Ihrer Website einbetten möchten.
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Vorschau</CardTitle>
            <CardDescription className="text-sm text-gray-500">
              Diese Vorschau zeigt die aktuelle Entwicklungsversion. Nach dem Deployment wird die Live-URL verwendet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden" style={{ height: '500px', backgroundColor: '#000000' }}>
              <iframe 
                src={`${BASE_URL}/portal?embed=full`} 
                style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#000000' }} 
                title="Portal Vorschau"
              />
            </div>
          </CardContent>
        </Card>

        {/* Iter 318: Per-Studio iFrame-Generator. Picker filtert die unten
            generierten Codes für Probetraining, Kursplan und Preisliste. */}
        <Card className="border-2 border-teal-200 bg-teal-50/30" data-testid="embed-studio-picker">
          <CardHeader>
            <div className="flex items-center gap-2">
              <span aria-hidden>📍</span>
              <CardTitle>Studio für Einbettung</CardTitle>
            </div>
            <CardDescription>
              Wähle aus, für welches Studio die Einbettungs-Codes (Probetraining, Kursplan, Preisliste) generiert werden.
              Die Kunden sehen dann nur Services, Trainer & Termine des gewählten Standorts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setEmbedStudioSlug('')}
                data-testid="embed-studio-all"
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                  embedStudioSlug === ''
                    ? 'bg-teal-600 text-white border-teal-700'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                }`}
              >
                Alle Studios (Kunde wählt selbst)
              </button>
              {embedStudios.map(s => (
                <button
                  key={s.studio_id}
                  type="button"
                  onClick={() => setEmbedStudioSlug(s.slug || s.studio_id)}
                  data-testid={`embed-studio-${s.slug || s.studio_id}`}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition ${
                    embedStudioSlug === (s.slug || s.studio_id)
                      ? 'bg-teal-600 text-white border-teal-700'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                  }`}
                >
                  📍 {s.short_name || s.name}
                </button>
              ))}
            </div>
            {embedStudioSlug && (
              <p className="mt-3 text-xs text-teal-700 bg-teal-100 rounded px-3 py-2">
                ✓ Aktiv: Alle Einbettungs-Codes enthalten <code className="font-mono">?studio={embedStudioSlug}</code> –
                Kunden auf der eingebetteten Seite sehen ausschließlich Daten dieses Standorts.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Public Booking Page - Probetraining */}
        <Card className="border-2 border-yellow-200 bg-yellow-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-yellow-600" />
              <CardTitle>Probetraining</CardTitle>
            </div>
            <CardDescription>
              Öffentliche Buchungsseite für Probetrainings ohne Login - zeigt nur Services mit "Nur einmal buchbar"
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Info about automatic filtering */}
            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 text-sm text-yellow-800">
              <strong>Automatische Filterung:</strong> Auf dieser Seite werden automatisch nur Services angezeigt, 
              die als "Nur einmal buchbar pro Kunde" markiert sind. Um einen Service hier anzuzeigen, aktivieren Sie 
              diese Option in den Service-Einstellungen.
            </div>

            {/* URL */}
            <div>
              <Label className="text-sm text-gray-600">Direkter Link</Label>
              <div className="flex gap-2 mt-1">
                <Input value={publicBookingUrl} readOnly className="font-mono text-sm flex-1" />
                <Button variant="outline" onClick={() => copyToClipboard(publicBookingUrl, true)}>
                  {copiedPublic ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="outline" onClick={() => window.open(publicBookingUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Embed Code */}
            <div>
              <Label className="text-sm text-gray-600">iFrame Einbettungscode</Label>
              <div className="relative mt-1">
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{publicBookingCode}</code>
                </pre>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2 bg-white"
                  onClick={() => copyToClipboard(publicBookingCode, true)}
                >
                  {copiedPublic ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 text-sm text-yellow-800">
              <strong>Tipp:</strong> Diese Seite zeigt automatisch nur Probetraining-Services (mit "Nur einmal buchbar" markiert). 
              Neukunden können einen Service auswählen, Termin buchen und erhalten automatisch ein Konto mit 
              Willkommens-E-Mail.
            </div>
          </CardContent>
        </Card>

        {/* Preview Public Booking */}
        <Card>
          <CardHeader>
            <CardTitle>Vorschau: Öffentliche Buchungsseite</CardTitle>
            <CardDescription className="text-sm text-gray-500">
              Diese Vorschau zeigt die aktuelle Entwicklungsversion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden" style={{ height: '500px', backgroundColor: '#000000' }}>
              <iframe 
                src={publicBookingUrl || `${BASE_URL}/buchen?embed=true`} 
                style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#000000' }} 
                title="Buchungsseite Vorschau"
              />
            </div>
          </CardContent>
        </Card>

        {/* Kursplan Embed */}
        <Card className="border-2 border-orange-200 bg-orange-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              <CardTitle>Kursplan (Group Fitness)</CardTitle>
            </div>
            <CardDescription>
              Wochenansicht aller Kurse für Ihre Website - Besucher können Kurse und Trainer sehen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* URL */}
            <div>
              <Label className="text-sm text-gray-600">Direkter Link</Label>
              <div className="flex gap-2 mt-1">
                <Input value={scheduleUrl} readOnly className="font-mono text-sm flex-1" />
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(scheduleUrl); setCopiedSchedule(true); setTimeout(() => setCopiedSchedule(false), 2000); toast.success('In Zwischenablage kopiert'); }}>
                  {copiedSchedule ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="outline" onClick={() => window.open(scheduleUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Embed Code */}
            <div>
              <Label className="text-sm text-gray-600">iFrame Einbettungscode</Label>
              <div className="relative mt-1">
                <pre className="bg-gray-900 text-orange-400 p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{scheduleCode}</code>
                </pre>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2 bg-white"
                  onClick={() => { navigator.clipboard.writeText(scheduleCode); setCopiedSchedule(true); setTimeout(() => setCopiedSchedule(false), 2000); toast.success('In Zwischenablage kopiert'); }}
                >
                  {copiedSchedule ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-orange-100 border border-orange-300 rounded-lg p-3 text-sm text-orange-800">
              <strong>Tipp:</strong> Der Kursplan zeigt automatisch alle geplanten Kurse der Woche an.
              Bei Klick auf einen Kurs sehen Besucher Details wie Uhrzeit, Trainer und Kontaktmöglichkeiten.
            </div>
          </CardContent>
        </Card>

        {/* Preisliste Embed */}
        <Card className="border-2 border-amber-200 bg-amber-50/30" data-testid="pricing-embed-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-amber-600" />
              <CardTitle>Preisliste</CardTitle>
            </div>
            <CardDescription>
              Öffentliche Preisliste mit Einzelpreisen, Punkte-Paketen und Abonnements - ideal zum Einbetten auf Ihrer Website
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* URL */}
            <div>
              <Label className="text-sm text-gray-600">Direkter Link</Label>
              <div className="flex gap-2 mt-1">
                <Input value={pricingUrl} readOnly className="font-mono text-sm flex-1" />
                <Button variant="outline" onClick={() => { navigator.clipboard.writeText(pricingUrl); setCopiedPricing(true); setTimeout(() => setCopiedPricing(false), 2000); toast.success('In Zwischenablage kopiert'); }}>
                  {copiedPricing ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button variant="outline" onClick={() => window.open(pricingUrl, '_blank')}>
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            {/* Embed Code */}
            <div>
              <Label className="text-sm text-gray-600">iFrame Einbettungscode</Label>
              <div className="relative mt-1">
                <pre className="bg-gray-900 text-amber-400 p-4 rounded-lg text-sm overflow-x-auto">
                  <code>{pricingCode}</code>
                </pre>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="absolute top-2 right-2 bg-white"
                  onClick={() => { navigator.clipboard.writeText(pricingCode); setCopiedPricing(true); setTimeout(() => setCopiedPricing(false), 2000); toast.success('In Zwischenablage kopiert'); }}
                >
                  {copiedPricing ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            
            {/* Info */}
            <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm text-amber-800">
              <strong>Tipp:</strong> Die Preisliste wird automatisch aus Ihren Services, Punkte-Paketen und Abo-Vorlagen generiert.
              Änderungen in den Einstellungen werden sofort auf der Preisliste sichtbar.
            </div>

            {/* Conversion Stats */}
            {clickStats.length > 0 && (
              <div data-testid="pricing-click-stats">
                <Label className="text-sm text-gray-600 mb-2 block">Conversion-Tracking: Probetraining-Klicks</Label>
                <div className="space-y-2">
                  {clickStats.map((stat, i) => {
                    const maxClicks = clickStats[0]?.clicks || 1;
                    const pct = Math.round((stat.clicks / maxClicks) * 100);
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-40 truncate font-medium">{stat.service_name}</span>
                        <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-sm font-semibold text-gray-800 w-16 text-right">{stat.clicks} Klicks</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Display Order Settings */}
            {displaySettings && (
              <div data-testid="pricing-display-settings">
                {/* Preislisten-Modus (06/2026): klassische Liste vs. geführter Dialog */}
                <div className="mb-5 rounded-lg border border-yellow-300 bg-yellow-50/60 p-4" data-testid="pricing-mode-card">
                  <Label className="text-sm font-semibold text-gray-800 mb-1 block">Anzeige-Modus der Preisliste</Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Klassische Liste (alle Preise auf einen Blick) oder geführter Frage-Dialog
                    (Kunde wählt Service, Gruppengröße & Häufigkeit → sieht seinen Preis inkl. Vorteil).
                  </p>
                  <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden bg-white mb-3">
                    {[['list', 'Klassische Liste'], ['guided', 'Geführter Dialog']].map(([val, lbl]) => (
                      <button
                        key={val}
                        onClick={() => setDisplaySettings({ ...displaySettings, pricing_mode: val })}
                        data-testid={`pricing-mode-${val}`}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          (displaySettings.pricing_mode || 'list') === val
                            ? 'bg-yellow-400 text-black' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >{lbl}</button>
                    ))}
                  </div>
                  {(displaySettings.pricing_mode === 'guided') && (
                    <div className="flex items-center gap-2 mb-3">
                      <Label className="text-sm text-gray-700">Rabatt ab dem 2. Training / Woche:</Label>
                      <input
                        type="number" min="0" max="90"
                        data-testid="pricing-second-discount"
                        value={displaySettings.second_session_discount_pct ?? 25}
                        onChange={(e) => setDisplaySettings({ ...displaySettings, second_session_discount_pct: e.target.value === '' ? '' : Number(e.target.value) })}
                        className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                  )}
                  <button
                    disabled={savingDisplay}
                    onClick={async () => {
                      setSavingDisplay(true);
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(`${API}/public/pricing/display-settings`, {
                          pricing_mode: displaySettings.pricing_mode || 'list',
                          second_session_discount_pct: displaySettings.second_session_discount_pct ?? 25,
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Anzeige-Modus gespeichert');
                      } catch (e) {
                        toast.error('Speichern fehlgeschlagen');
                      } finally {
                        setSavingDisplay(false);
                      }
                    }}
                    data-testid="save-pricing-mode-btn"
                    className="rounded-md bg-black text-white text-sm px-4 py-2 disabled:opacity-50"
                  >
                    {savingDisplay ? 'Speichern…' : 'Modus speichern'}
                  </button>
                </div>

                <Label className="text-sm text-gray-600 mb-2 block">Reihenfolge der Sektionen</Label>
                <div className="space-y-1.5">
                  {(displaySettings.section_order || ['services', 'point_packages', 'credit_packages', 'recurring_subs']).map((key, idx, arr) => {
                    const labels = { services: 'Einzelpreise', point_packages: 'Punkt-Pakete', credit_packages: '10er-Abos', recurring_subs: 'Wiederkehrende Abos' };
                    return (
                      <div key={key} className="flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2">
                        <GripVertical className="w-4 h-4 text-gray-300" />
                        <span className="text-sm flex-1 font-medium text-gray-700">{labels[key] || key}</span>
                        <button
                          disabled={idx === 0}
                          onClick={() => {
                            const newOrder = [...arr];
                            [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
                            setDisplaySettings({ ...displaySettings, section_order: newOrder });
                          }}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-20"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          disabled={idx === arr.length - 1}
                          onClick={() => {
                            const newOrder = [...arr];
                            [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
                            setDisplaySettings({ ...displaySettings, section_order: newOrder });
                          }}
                          className="p-1 rounded hover:bg-gray-200 disabled:opacity-20"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <Label className="text-sm text-gray-600 mb-2 block mt-4">Reihenfolge der Kategorien</Label>
                <div className="space-y-1.5">
                  {(displaySettings.category_order || []).map((cat, idx, arr) => (
                    <div key={cat} className="flex items-center gap-2 bg-gray-50 border rounded-lg px-3 py-2">
                      <GripVertical className="w-4 h-4 text-gray-300" />
                      <span className="text-sm flex-1 font-medium text-gray-700">{cat}</span>
                      <button
                        disabled={idx === 0}
                        onClick={() => {
                          const newOrder = [...arr];
                          [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
                          setDisplaySettings({ ...displaySettings, category_order: newOrder });
                        }}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-20"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        disabled={idx === arr.length - 1}
                        onClick={() => {
                          const newOrder = [...arr];
                          [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
                          setDisplaySettings({ ...displaySettings, category_order: newOrder });
                        }}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-20"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Service Order within categories */}
                {displaySettings.service_order && (
                  <>
                    <Label className="text-sm text-gray-600 mb-2 block mt-4">Reihenfolge der Services (pro Kategorie)</Label>
                    {(displaySettings.category_order || Object.keys(displaySettings.service_order || {})).map(catName => {
                      const serviceIds = displaySettings.service_order?.[catName] || [];
                      if (serviceIds.length <= 1) return null;
                      const nameMap = displaySettings.service_names || {};
                      return (
                        <div key={catName} className="mb-3" data-testid={`service-order-${catName}`}>
                          <p className="text-xs font-semibold text-gray-500 mb-1">{catName}</p>
                          <div className="space-y-1">
                            {serviceIds.map((sid, idx) => (
                              <div key={sid} className="flex items-center gap-2 bg-white border rounded px-2.5 py-1.5">
                                <GripVertical className="w-3.5 h-3.5 text-gray-300" />
                                <span className="text-xs flex-1 text-gray-700">{nameMap[sid] || sid.slice(0, 12) + '...'}</span>
                                <button
                                  disabled={idx === 0}
                                  onClick={() => {
                                    const newIds = [...serviceIds];
                                    [newIds[idx - 1], newIds[idx]] = [newIds[idx], newIds[idx - 1]];
                                    setDisplaySettings({
                                      ...displaySettings,
                                      service_order: { ...displaySettings.service_order, [catName]: newIds }
                                    });
                                  }}
                                  className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20"
                                >
                                  <ArrowUp className="w-3 h-3" />
                                </button>
                                <button
                                  disabled={idx === serviceIds.length - 1}
                                  onClick={() => {
                                    const newIds = [...serviceIds];
                                    [newIds[idx], newIds[idx + 1]] = [newIds[idx + 1], newIds[idx]];
                                    setDisplaySettings({
                                      ...displaySettings,
                                      service_order: { ...displaySettings.service_order, [catName]: newIds }
                                    });
                                  }}
                                  className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-20"
                                >
                                  <ArrowDown className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <Button
                  className="mt-3 w-full"
                  disabled={savingDisplay}
                  onClick={async () => {
                    setSavingDisplay(true);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(`${API}/public/pricing/display-settings`, displaySettings, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Reihenfolge gespeichert');
                    } catch (e) {
                      toast.error('Fehler beim Speichern');
                    } finally {
                      setSavingDisplay(false);
                    }
                  }}
                  data-testid="save-display-settings-btn"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {savingDisplay ? 'Speichern...' : 'Reihenfolge speichern'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Iframe-Größe: Globaler +/- Scale + Per-Element Feintuning */}
        <EmbedStyleCard
          API={API}
          BASE_URL={BASE_URL}
          displaySettings={displaySettings}
          setDisplaySettings={setDisplaySettings}
        />

        {/* Pricing Preview */}
        <Card>
          <CardHeader>
            <CardTitle>Vorschau: Preisliste</CardTitle>
            <CardDescription className="text-sm text-gray-500">
              Diese Vorschau zeigt die aktuelle Entwicklungsversion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden" style={{ height: '500px', backgroundColor: '#000000' }}>
              <iframe 
                src={`${BASE_URL}/preisliste?embed=true`} 
                style={{ width: '100%', height: '100%', border: 'none', backgroundColor: '#000000' }} 
                title="Preisliste Vorschau"
              />
            </div>
          </CardContent>
        </Card>

        {/* KI Chat Widget */}
        <Card className="border-2 border-purple-200 bg-purple-50/30">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-purple-600" />
              <CardTitle>🤖 KI Chat Widget</CardTitle>
            </div>
            <CardDescription>
              Einbettbarer Chatbot für Ihre Website - Kunden können Fragen stellen, Termine buchen und sich auf die Warteliste setzen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable Widget */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
              <div>
                <Label className="text-base font-medium">Widget aktiviert</Label>
                <p className="text-sm text-gray-500">Chat auf Ihrer Website anzeigen</p>
              </div>
              <Switch
                checked={widgetSettings.enabled}
                onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* Widget Title & Welcome Message */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Widget-Titel</Label>
                <Input
                  value={widgetSettings.widget_title}
                  onChange={(e) => setWidgetSettings(prev => ({ ...prev, widget_title: e.target.value }))}
                  placeholder="StrongFit Chat"
                />
              </div>
              <div className="space-y-2">
                <Label>Willkommensnachricht</Label>
                <Input
                  value={widgetSettings.welcome_message}
                  onChange={(e) => setWidgetSettings(prev => ({ ...prev, welcome_message: e.target.value }))}
                  placeholder="Hallo! 👋 Wie kann ich dir helfen?"
                />
              </div>
            </div>

            {/* Verification Settings */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-purple-600" />
                <Label className="text-base font-medium">Verifizierung (nur für Buchung)</Label>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <span className="font-medium">Verifizierung vor Buchung</span>
                  <p className="text-xs text-gray-500">Chat ist frei - nur für Terminbuchung nötig</p>
                </div>
                <Switch
                  checked={widgetSettings.verification_required_for_booking}
                  onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, verification_required_for_booking: checked }))}
                />
              </div>

              {widgetSettings.verification_required_for_booking && (
                <div className="grid grid-cols-2 gap-3">
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      widgetSettings.verification_methods?.includes('email') 
                        ? 'border-purple-400 bg-purple-100' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => toggleWidgetVerificationMethod('email')}
                  >
                    <div className="flex items-center gap-2">
                      <Mail className={`h-4 w-4 ${widgetSettings.verification_methods?.includes('email') ? 'text-purple-600' : 'text-gray-400'}`} />
                      <span className="font-medium">E-Mail</span>
                    </div>
                  </div>
                  <div 
                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      widgetSettings.verification_methods?.includes('whatsapp') 
                        ? 'border-green-400 bg-green-100' 
                        : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => toggleWidgetVerificationMethod('whatsapp')}
                  >
                    <div className="flex items-center gap-2">
                      <MessageCircle className={`h-4 w-4 ${widgetSettings.verification_methods?.includes('whatsapp') ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="font-medium">WhatsApp</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-3">
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <span className="text-sm">FAQ</span>
                <Switch
                  checked={widgetSettings.faq_enabled}
                  onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, faq_enabled: checked }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <span className="text-sm">Buchung</span>
                <Switch
                  checked={widgetSettings.booking_enabled}
                  onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, booking_enabled: checked }))}
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <span className="text-sm">Warteliste</span>
                <Switch
                  checked={widgetSettings.waitlist_enabled}
                  onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, waitlist_enabled: checked }))}
                />
              </div>
            </div>

            {/* Design */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-purple-600" />
                <Label className="text-base font-medium">Design</Label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Akzentfarbe</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={widgetSettings.primary_color}
                      onChange={(e) => setWidgetSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={widgetSettings.primary_color}
                      onChange={(e) => setWidgetSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Hintergrund</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={widgetSettings.background_color}
                      onChange={(e) => setWidgetSettings(prev => ({ ...prev, background_color: e.target.value }))}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={widgetSettings.background_color}
                      onChange={(e) => setWidgetSettings(prev => ({ ...prev, background_color: e.target.value }))}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-purple-600" />
                <Label className="text-base font-medium">Social Media Links</Label>
              </div>
              <p className="text-xs text-gray-500">Diese Links werden im Popup-Widget angezeigt</p>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-[#25D366] flex items-center justify-center">
                      <MessageCircle className="w-3 h-3 text-white" />
                    </span>
                    WhatsApp
                  </Label>
                  <Input
                    value={widgetSettings.whatsapp_number || ''}
                    onChange={(e) => setWidgetSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                    placeholder="41764449996"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-[#E4405F] flex items-center justify-center">
                      <span className="text-white text-xs">📷</span>
                    </span>
                    Instagram
                  </Label>
                  <Input
                    value={widgetSettings.instagram_handle || ''}
                    onChange={(e) => setWidgetSettings(prev => ({ ...prev, instagram_handle: e.target.value }))}
                    placeholder="strongfit_wetzikon"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-[#1877F2] flex items-center justify-center">
                      <span className="text-white text-xs">f</span>
                    </span>
                    Facebook
                  </Label>
                  <Input
                    value={widgetSettings.facebook_page || ''}
                    onChange={(e) => setWidgetSettings(prev => ({ ...prev, facebook_page: e.target.value }))}
                    placeholder="strongfitwetzikon"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveWidgetSettings} disabled={savingWidget} className="bg-purple-600 hover:bg-purple-700">
                {savingWidget ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Einstellungen speichern
              </Button>
            </div>

            <hr className="border-purple-200" />

            {/* Embed Codes */}
            <div className="space-y-4">
              <Label className="text-base font-medium">Einbettungscodes</Label>
              
              {/* Popup Widget Code */}
              <div>
                <Label className="text-sm text-gray-600">🔘 Popup-Widget (runder Button)</Label>
                <div className="relative mt-1">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto">
                    <code>{`<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${BASE_URL}/widget/popup';
  iframe.style.cssText = 'position:fixed;bottom:0;right:0;width:450px;height:650px;border:none;z-index:9999;background:transparent;pointer-events:none;';
  iframe.setAttribute('allowtransparency', 'true');
  iframe.onload = function() {
    iframe.style.pointerEvents = 'auto';
  };
  document.body.appendChild(iframe);
})();
</script>`}</code>
                  </pre>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="absolute top-2 right-2 bg-white"
                    onClick={() => copyWidgetCode(`<script>
(function() {
  var iframe = document.createElement('iframe');
  iframe.src = '${BASE_URL}/widget/popup';
  iframe.style.cssText = 'position:fixed;bottom:0;right:0;width:450px;height:650px;border:none;z-index:9999;background:transparent;pointer-events:none;';
  iframe.setAttribute('allowtransparency', 'true');
  iframe.onload = function() {
    iframe.style.pointerEvents = 'auto';
  };
  document.body.appendChild(iframe);
})();
</script>`, 'popup')}
                  >
                    {copiedWidget === 'popup' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Direct URL */}
              <div>
                <Label className="text-sm text-gray-600">Direkter Chat-Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={widgetEmbedCode.widget_url || `${BASE_URL}/widget/chat`} readOnly className="font-mono text-sm flex-1" />
                  <Button variant="outline" onClick={() => copyWidgetCode(widgetEmbedCode.widget_url || `${BASE_URL}/widget/chat`, 'url')}>
                    {copiedWidget === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button variant="outline" onClick={() => window.open(widgetEmbedCode.widget_url || `${BASE_URL}/widget/chat`, '_blank')}>
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Demo Link */}
              <div>
                <Label className="text-sm text-gray-600">🎬 Demo-Seite (zeigt Widget auf Beispiel-Website)</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={`${BASE_URL}/widget/demo`} readOnly className="font-mono text-sm flex-1" />
                  <Button variant="outline" onClick={() => window.open(`${BASE_URL}/widget/demo`, '_blank')}>
                    <Eye className="h-4 w-4 mr-1" /> Demo öffnen
                  </Button>
                </div>
              </div>

              {/* iframe Code */}
              <div>
                <Label className="text-sm text-gray-600">iFrame Einbettungscode</Label>
                <div className="relative mt-1">
                  <pre className="bg-gray-900 text-purple-400 p-4 rounded-lg text-sm overflow-x-auto">
                    <code>{widgetEmbedCode.iframe_code || `<iframe src="${BASE_URL}/widget/chat" style="border: none; width: 400px; height: 600px; border-radius: 16px;" allow="clipboard-write"></iframe>`}</code>
                  </pre>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="absolute top-2 right-2 bg-white"
                    onClick={() => copyWidgetCode(widgetEmbedCode.iframe_code || `<iframe src="${BASE_URL}/widget/chat" style="border: none; width: 400px; height: 600px; border-radius: 16px;" allow="clipboard-write"></iframe>`, 'iframe')}
                  >
                    {copiedWidget === 'iframe' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-800">
              <strong>💡 Tipp:</strong> Der KI-Chatbot verwendet die gleiche Wissensbasis wie der WhatsApp-Bot. 
              Konfigurieren Sie diese unter <a href="/chatbot-settings" className="underline font-medium">KI Chatbot Einstellungen</a>.
            </div>
          </CardContent>
        </Card>

        {/* Widget Preview - Button only to avoid auto-scroll */}
        <Card>
          <CardHeader>
            <CardTitle>Vorschau: KI Chat Widget</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-4">
              <p className="text-gray-500 text-sm text-center">
                Klicken Sie auf den Button um eine Live-Vorschau des Widgets zu öffnen
              </p>
              <Button 
                onClick={() => window.open(`${BASE_URL}/widget/chat`, '_blank', 'width=420,height=700')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Eye className="h-4 w-4 mr-2" />
                Widget-Vorschau öffnen
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortalEmbedSettings;
